/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AmrUtilizationStatus, SupplementalLegType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RendezvousRequestProps {
  id: string;
  type: SupplementalLegType;
  agencyLocationId: string | null;
  agencyEta: Date | null;
  rendezvousLocationDescription: string | null;
  contactInformation: string | null;
  confirmedAt: Date | null;
  departedLzAt: Date | null;
  arrivedDestAt: Date | null;
  departedDestAt: Date | null;
  arrivedLzAt: Date | null;
  cancelledAt: Date | null;
  notes: string | null;
  utilizationStatus: AmrUtilizationStatus;
  transportUnitNumber: string | null;
  latestStatus: string | null;
  latestStatusTimestamp: Date | null;
  requestLocationId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RendezvousRequestRecord extends Record<RendezvousRequestProps>({
  id: null as any,
  type: null as any,
  agencyLocationId: null as any,
  agencyEta: null as any,
  rendezvousLocationDescription: null as any,
  contactInformation: null as any,
  confirmedAt: null as any,
  departedLzAt: null as any,
  arrivedDestAt: null as any,
  departedDestAt: null as any,
  arrivedLzAt: null as any,
  cancelledAt: null as any,
  notes: null as any,
  utilizationStatus: null as any,
  transportUnitNumber: null as any,
  latestStatus: null as any,
  latestStatusTimestamp: null as any,
  requestLocationId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: SupplementalLegType;
    agencyLocationId: string | null;
    agencyEta: string | number | Date | null;
    rendezvousLocationDescription: string | null;
    contactInformation: string | null;
    confirmedAt: string | number | Date | null;
    departedLzAt: string | number | Date | null;
    arrivedDestAt: string | number | Date | null;
    departedDestAt: string | number | Date | null;
    arrivedLzAt: string | number | Date | null;
    cancelledAt: string | number | Date | null;
    notes: string | null;
    utilizationStatus: AmrUtilizationStatus;
    transportUnitNumber: string | null;
    latestStatus: string | null;
    latestStatusTimestamp: string | number | Date | null;
    requestLocationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RendezvousRequestRecord {
    if (json == null) {
      throw new Error('RendezvousRequestRecord.fromJS: parameter cannot be null');
    }
    return new RendezvousRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RendezvousRequestJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', SupplementalLegType.ExternalAgency),
        'RendezvousRequestJson.type',
      ),
      agencyLocationId: json.agencyLocationId,
      agencyEta: json.agencyEta != null ? new Date(json.agencyEta) : null,
      rendezvousLocationDescription: json.rendezvousLocationDescription,
      contactInformation: json.contactInformation,
      confirmedAt: json.confirmedAt != null ? new Date(json.confirmedAt) : null,
      departedLzAt: json.departedLzAt != null ? new Date(json.departedLzAt) : null,
      arrivedDestAt: json.arrivedDestAt != null ? new Date(json.arrivedDestAt) : null,
      departedDestAt: json.departedDestAt != null ? new Date(json.departedDestAt) : null,
      arrivedLzAt: json.arrivedLzAt != null ? new Date(json.arrivedLzAt) : null,
      cancelledAt: json.cancelledAt != null ? new Date(json.cancelledAt) : null,
      notes: json.notes,
      utilizationStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'utilizationStatus', AmrUtilizationStatus.Unspecified),
        'RendezvousRequestJson.utilizationStatus',
      ),
      transportUnitNumber: json.transportUnitNumber,
      latestStatus: json.latestStatus,
      latestStatusTimestamp:
        json.latestStatusTimestamp != null ? new Date(json.latestStatusTimestamp) : null,
      requestLocationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestLocationId', '00000000-0000-0000-0000-000000000000'),
        'RendezvousRequestJson.requestLocationId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RendezvousRequestJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: SupplementalLegType;
    agencyLocationId: string | null;
    agencyEta: string | number | Date | null;
    rendezvousLocationDescription: string | null;
    contactInformation: string | null;
    confirmedAt: string | number | Date | null;
    departedLzAt: string | number | Date | null;
    arrivedDestAt: string | number | Date | null;
    departedDestAt: string | number | Date | null;
    arrivedLzAt: string | number | Date | null;
    cancelledAt: string | number | Date | null;
    notes: string | null;
    utilizationStatus: AmrUtilizationStatus;
    transportUnitNumber: string | null;
    latestStatus: string | null;
    latestStatusTimestamp: string | number | Date | null;
    requestLocationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RendezvousRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('RendezvousRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RendezvousRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RendezvousRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', SupplementalLegType.ExternalAgency),
            'RendezvousRequestJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyLocationId: tryOrDefault(
        () => json.agencyLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      agencyEta: tryOrDefault(
        () => (json.agencyEta != null ? new Date(json.agencyEta) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rendezvousLocationDescription: tryOrDefault(
        () => json.rendezvousLocationDescription,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      contactInformation: tryOrDefault(
        () => json.contactInformation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      confirmedAt: tryOrDefault(
        () => (json.confirmedAt != null ? new Date(json.confirmedAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departedLzAt: tryOrDefault(
        () => (json.departedLzAt != null ? new Date(json.departedLzAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivedDestAt: tryOrDefault(
        () => (json.arrivedDestAt != null ? new Date(json.arrivedDestAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      departedDestAt: tryOrDefault(
        () => (json.departedDestAt != null ? new Date(json.departedDestAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      arrivedLzAt: tryOrDefault(
        () => (json.arrivedLzAt != null ? new Date(json.arrivedLzAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancelledAt: tryOrDefault(
        () => (json.cancelledAt != null ? new Date(json.cancelledAt) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      utilizationStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'utilizationStatus', AmrUtilizationStatus.Unspecified),
            'RendezvousRequestJson.utilizationStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportUnitNumber: tryOrDefault(
        () => json.transportUnitNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestStatus: tryOrDefault(
        () => json.latestStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      latestStatusTimestamp: tryOrDefault(
        () => (json.latestStatusTimestamp != null ? new Date(json.latestStatusTimestamp) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestLocationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'requestLocationId', '00000000-0000-0000-0000-000000000000'),
            'RendezvousRequestJson.requestLocationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RendezvousRequestJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RendezvousRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('RendezvousRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RendezvousRequestViewModelBase implements RendezvousRequestProps {
  protected readonly _props: RendezvousRequestProps;
  private _json?: RendezvousRequestProps;

  get id(): string {
    return this._props.id;
  }
  get type(): SupplementalLegType {
    return this._props.type;
  }
  get agencyLocationId(): string | null {
    return this._props.agencyLocationId;
  }
  get agencyEta(): Date | null {
    return this._props.agencyEta;
  }
  get rendezvousLocationDescription(): string | null {
    return this._props.rendezvousLocationDescription;
  }
  get contactInformation(): string | null {
    return this._props.contactInformation;
  }
  get confirmedAt(): Date | null {
    return this._props.confirmedAt;
  }
  get departedLzAt(): Date | null {
    return this._props.departedLzAt;
  }
  get arrivedDestAt(): Date | null {
    return this._props.arrivedDestAt;
  }
  get departedDestAt(): Date | null {
    return this._props.departedDestAt;
  }
  get arrivedLzAt(): Date | null {
    return this._props.arrivedLzAt;
  }
  get cancelledAt(): Date | null {
    return this._props.cancelledAt;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get utilizationStatus(): AmrUtilizationStatus {
    return this._props.utilizationStatus;
  }
  get transportUnitNumber(): string | null {
    return this._props.transportUnitNumber;
  }
  get latestStatus(): string | null {
    return this._props.latestStatus;
  }
  get latestStatusTimestamp(): Date | null {
    return this._props.latestStatusTimestamp;
  }
  get requestLocationId(): string {
    return this._props.requestLocationId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCompanyUnit() {
    return this._props.type === SupplementalLegType.CompanyUnit;
  }
  public get isExternalAgency() {
    return this._props.type === SupplementalLegType.ExternalAgency;
  }
  public get isSisterCompanyUnit() {
    return this._props.type === SupplementalLegType.SisterCompanyUnit;
  }
  public get isNoResourcesAvailable() {
    return this._props.utilizationStatus === AmrUtilizationStatus.NoResourcesAvailable;
  }
  public get isNoServiceAvailable() {
    return this._props.utilizationStatus === AmrUtilizationStatus.NoServiceAvailable;
  }
  public get isNotConsidered() {
    return this._props.utilizationStatus === AmrUtilizationStatus.NotConsidered;
  }
  public get isUnsatisfactoryEta() {
    return this._props.utilizationStatus === AmrUtilizationStatus.UnsatisfactoryEta;
  }
  public get isUnspecified() {
    return this._props.utilizationStatus === AmrUtilizationStatus.Unspecified;
  }
  public get isUtilized() {
    return this._props.utilizationStatus === AmrUtilizationStatus.Utilized;
  }

  public isAnyType(...supplementalLegType: SupplementalLegType[]) {
    return supplementalLegType.includes(this._props.type);
  }
  public isAnyUtilizationStatus(...amrUtilizationStatus: AmrUtilizationStatus[]) {
    return amrUtilizationStatus.includes(this._props.utilizationStatus);
  }

  constructor(props: RendezvousRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        agencyLocationId: this._props.agencyLocationId,
        agencyEta: this._props.agencyEta,
        rendezvousLocationDescription: this._props.rendezvousLocationDescription,
        contactInformation: this._props.contactInformation,
        confirmedAt: this._props.confirmedAt,
        departedLzAt: this._props.departedLzAt,
        arrivedDestAt: this._props.arrivedDestAt,
        departedDestAt: this._props.departedDestAt,
        arrivedLzAt: this._props.arrivedLzAt,
        cancelledAt: this._props.cancelledAt,
        notes: this._props.notes,
        utilizationStatus: this._props.utilizationStatus,
        transportUnitNumber: this._props.transportUnitNumber,
        latestStatus: this._props.latestStatus,
        latestStatusTimestamp: this._props.latestStatusTimestamp,
        requestLocationId: this._props.requestLocationId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRendezvousRequestEntities = (state: AppState) =>
  state.entityStores.rendezvousRequest.entities;

export const selectRendezvousRequestById = (id: string) => (state: AppState) =>
  state.entityStores.rendezvousRequest.entities.get(id);

export const selectRendezvousRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.rendezvousRequest.meta.get(id);

export const selectRendezvousRequestAndMetadataById = (id: string) =>
  createSelector(
    selectRendezvousRequestById(id),
    selectRendezvousRequestMetadataById(id),
    (rendezvousRequest, meta) => ({ rendezvousRequest, meta }),
  );

export function selectRendezvousRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRendezvousRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RendezvousRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRendezvousRequestEntitiesByType(type: SupplementalLegType) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByAgencyLocationId(agencyLocationId: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.agencyLocationId === agencyLocationId).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByAgencyEta(agencyEta: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.agencyEta === agencyEta).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByRendezvousLocationDescription(
  rendezvousLocationDescription: string,
) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities
      .filter((e) => e.rendezvousLocationDescription === rendezvousLocationDescription)
      .valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByContactInformation(contactInformation: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.contactInformation === contactInformation).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByConfirmedAt(confirmedAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.confirmedAt === confirmedAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByDepartedLzAt(departedLzAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.departedLzAt === departedLzAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByArrivedDestAt(arrivedDestAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.arrivedDestAt === arrivedDestAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByDepartedDestAt(departedDestAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.departedDestAt === departedDestAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByArrivedLzAt(arrivedLzAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.arrivedLzAt === arrivedLzAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByCancelledAt(cancelledAt: Date) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.cancelledAt === cancelledAt).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByNotes(notes: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByUtilizationStatus(
  utilizationStatus: AmrUtilizationStatus,
) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.utilizationStatus === utilizationStatus).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByTransportUnitNumber(transportUnitNumber: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.transportUnitNumber === transportUnitNumber).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByLatestStatus(latestStatus: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.latestStatus === latestStatus).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByLatestStatusTimestamp(
  latestStatusTimestamp: Date,
) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.latestStatusTimestamp === latestStatusTimestamp).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByRequestLocationId(requestLocationId: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.requestLocationId === requestLocationId).valueSeq(),
  );
}

export function selectRendezvousRequestEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRendezvousRequestEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
